import { useState, useEffect } from "react";
import { TattooImage } from "@/data/tattoos";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Edit, GripVertical, Loader, Save, Trash2 } from "lucide-react";
import { useTattoos } from "@/hooks/useTattoos";
import { useToast } from "@/hooks/use-toast";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

interface TattooTableProps {
  tattoos: TattooImage[];
  onEdit: (tattoo: TattooImage) => void;
  onDelete: (id: string) => void;
  isLoading: boolean;
}

const TattooTable = ({ tattoos, onEdit, onDelete, isLoading }: TattooTableProps) => {
  const [orderedTattoos, setOrderedTattoos] = useState<TattooImage[]>(tattoos);
  const [reorderMode, setReorderMode] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  
  const { updateTattoosOrder } = useTattoos();
  const { toast } = useToast();
  
  // Atualizar a lista ordenada quando as tatuagens mudarem
  useEffect(() => {
    setOrderedTattoos(tattoos);
  }, [tattoos]);
  
  // Lidar com o fim do drag and drop
  const handleDragEnd = (result: any) => {
    // Se não foi solto em uma área válida, não fazer nada
    if (!result.destination) return;
    
    // Se a posição é a mesma, não fazer nada
    if (result.destination.index === result.source.index) return;
    
    // Reordenar a lista
    const items = Array.from(orderedTattoos);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setOrderedTattoos(items);
  };
  
  // Salvar a ordem atual no banco de dados
  const saveOrder = async () => {
    if (orderedTattoos.length === 0) return;
    
    try {
      setSavingOrder(true);
      
      // Extrair os IDs na nova ordem
      const orderedIds = orderedTattoos
        .map(tattoo => {
          if (typeof tattoo.id === 'string') {
            return tattoo.id;
          }
          return null;
        })
        .filter((id): id is string => id !== null);
      
      const result = await updateTattoosOrder(orderedIds);
      
      if (result.success) {
        toast({
          title: "Ordem atualizada",
          description: "A ordem das tatuagens foi salva com sucesso.",
        });
        setReorderMode(false);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar ordem",
        description: `Não foi possível salvar a ordem: ${(error as Error).message}`,
      });
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div className="rounded-lg border border-magicmoon-purple/20 bg-magicmoon-darkPurple/30 overflow-hidden">
      <div className="px-6 py-4 border-b border-magicmoon-purple/20 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-artistic text-white">Portfólio de Tatuagens</h2>
          <p className="text-gray-400">Gerencie as tatuagens exibidas em seu portfólio.</p>
        </div>
        <div className="flex items-center gap-3">
          {!reorderMode ? (
            <Button 
              variant="outline" 
              onClick={() => setReorderMode(true)}
              disabled={isLoading || tattoos.length <= 1}
            >
              Reorganizar
            </Button>
          ) : (
            <>
              <Button 
                variant="ghost" 
                onClick={() => {
                  setReorderMode(false);
                  setOrderedTattoos(tattoos);
                }}
                disabled={savingOrder}
              >
                Cancelar
              </Button>
              <Button 
                variant="default"
                onClick={saveOrder}
                disabled={savingOrder}
              >
                {savingOrder ? (
                  <>
                    <Loader size={16} className="mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Salvar Ordem
                  </>
                )}
              </Button>
            </>
          )}
          {(isLoading || savingOrder) && (
            <div className="flex items-center gap-2 text-magicmoon-purple">
              <Loader size={16} className="animate-spin" />
              <span className="text-sm">Processando...</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table>
            <TableHeader>
              <TableRow>
                {reorderMode && <TableHead className="w-10"></TableHead>}
                <TableHead className="w-20">Imagem</TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Estilo</TableHead>
                <TableHead>Técnica</TableHead>
                <TableHead>Parte do Corpo</TableHead>
                <TableHead>Cores</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            
            {orderedTattoos.length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={reorderMode ? 8 : 7} className="text-center py-8 text-gray-400">
                    Nenhuma tatuagem encontrada. Clique em "Nova Tatuagem" para adicionar.
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <Droppable droppableId="tattoos" isDropDisabled={!reorderMode}>
                {(provided) => (
                  <TableBody
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {orderedTattoos.map((tattoo, index) => (
                      <Draggable 
                        key={String(tattoo.id)} 
                        draggableId={String(tattoo.id)} 
                        index={index}
                        isDragDisabled={!reorderMode}
                      >
                        {(provided, snapshot) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${
                              snapshot.isDragging ? 'bg-magicmoon-purple/10' : 'hover:bg-magicmoon-purple/5'
                            }`}
                          >
                            {reorderMode && (
                              <TableCell className="w-10 text-center">
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-grab active:cursor-grabbing w-full flex justify-center"
                                >
                                  <GripVertical className="text-gray-500" size={16} />
                                </div>
                              </TableCell>
                            )}
                            <TableCell>
                              <div className="h-16 w-16 rounded overflow-hidden">
                                <img 
                                  src={tattoo.thumbnail} 
                                  alt={tattoo.title}
                                  className="h-full w-full object-cover" 
                                />
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{tattoo.title}</TableCell>
                            <TableCell>{tattoo.style}</TableCell>
                            <TableCell>{tattoo.technique}</TableCell>
                            <TableCell>{tattoo.bodyPart}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                tattoo.isColor 
                                  ? "bg-magicmoon-purple/20 text-magicmoon-purple" 
                                  : "bg-gray-700/50 text-gray-300"
                              }`}>
                                {tattoo.isColor ? "Colorida" : "Preto e Branco"}
                              </span>
                            </TableCell>
                            <TableCell className="text-right space-x-2">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => onEdit(tattoo)}
                                className="text-gray-400 hover:text-white"
                                disabled={isLoading || reorderMode}
                              >
                                <Edit size={16} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => {
                                  if (typeof tattoo.id === 'string') {
                                    onDelete(tattoo.id);
                                  } else {
                                    console.error("ID da tatuagem não é uma string válida", tattoo.id);
                                  }
                                }}
                                className="text-gray-400 hover:text-destructive"
                                disabled={isLoading || reorderMode}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            )}
          </Table>
        </DragDropContext>
      </div>
    </div>
  );
};

export default TattooTable;
